import React from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  FormControlLabel,
  Switch,
  FormGroup,
  Checkbox,
  FormHelperText,
  Alert,
  Grid,
} from '@mui/material';
import {
  RESERVABLE_CATEGORIES,
  DURATION_PRESETS,
} from '../../utils/reservableHelpers';
import AvailabilityWeekGrid from './AvailabilityWeekGrid';

const ReservableFormFields = ({ form, errors, teachers, onChange }) => {
  const setField = (field, value) => onChange({ ...form, [field]: value });

  const toggleDuration = (duration) => {
    const current = form.availableDurations || [];
    const next = current.includes(duration)
      ? current.filter((d) => d !== duration)
      : [...current, duration].sort((a, b) => a - b);
    setField('availableDurations', next);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h6">Informazioni generali</Typography>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 8 }}>
          <TextField
            fullWidth
            label="Nome risorsa"
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
            error={Boolean(errors.name)}
            helperText={errors.name}
            required
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <FormControl fullWidth error={Boolean(errors.category)}>
            <InputLabel>Categoria</InputLabel>
            <Select
              value={form.category}
              label="Categoria"
              onChange={(e) => setField('category', e.target.value)}
            >
              {RESERVABLE_CATEGORIES.map((cat) => (
                <MenuItem key={cat.value} value={cat.value}>
                  {cat.label}
                </MenuItem>
              ))}
            </Select>
            {errors.category && <FormHelperText>{errors.category}</FormHelperText>}
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label="Descrizione"
            value={form.description}
            onChange={(e) => setField('description', e.target.value)}
            multiline
            minRows={2}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Location / area"
            value={form.location}
            onChange={(e) => setField('location', e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControlLabel
            control={
              <Switch
                checked={form.isActive}
                onChange={(e) => setField('isActive', e.target.checked)}
              />
            }
            label="Risorsa attiva (visibile per prenotazioni)"
          />
        </Grid>
      </Grid>

      {form.category === 'istruttore' && (
        <FormControl fullWidth error={Boolean(errors.teacherId)} required>
          <InputLabel>Istruttore</InputLabel>
          <Select
            value={form.teacherId}
            label="Istruttore"
            onChange={(e) => setField('teacherId', e.target.value)}
          >
            <MenuItem value="">
              <em>Seleziona istruttore</em>
            </MenuItem>
            {teachers.map((teacher) => (
              <MenuItem key={teacher._id} value={teacher._id}>
                {teacher.name} {teacher.surname} ({teacher.email})
              </MenuItem>
            ))}
          </Select>
          {errors.teacherId && <FormHelperText>{errors.teacherId}</FormHelperText>}
        </FormControl>
      )}

      <Typography variant="h6">Capacità e prezzi</Typography>

      <Alert severity="info" sx={{ mb: 0 }}>
        La capacità è solo informativa. La concorrenza effettiva è gestita da
        &quot;Accessi contemporanei&quot; (es. torre con 6 accessi paralleli).
      </Alert>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            fullWidth
            type="number"
            label="Capacità (informativa)"
            value={form.capacity}
            onChange={(e) => setField('capacity', e.target.value)}
            error={Boolean(errors.capacity)}
            helperText={errors.capacity}
            inputProps={{ min: 1, max: 100 }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            fullWidth
            type="number"
            label="Accessi contemporanei"
            value={form.maxConcurrentAccess}
            onChange={(e) => setField('maxConcurrentAccess', e.target.value)}
            error={Boolean(errors.maxConcurrentAccess)}
            helperText={errors.maxConcurrentAccess || 'Slot paralleli sulla stessa fascia'}
            inputProps={{ min: 1, max: 100 }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            fullWidth
            type="number"
            label="Costo orario (€)"
            value={form.costPerHour}
            onChange={(e) => setField('costPerHour', e.target.value)}
            error={Boolean(errors.costPerHour)}
            helperText={errors.costPerHour}
            inputProps={{ min: 0, step: 0.5 }}
            required
          />
        </Grid>
      </Grid>

      <Box>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
          Durate prenotabili
        </Typography>
        <FormGroup row>
          {DURATION_PRESETS.map((preset) => (
            <FormControlLabel
              key={preset.value}
              control={
                <Checkbox
                  checked={form.availableDurations?.includes(preset.value)}
                  onChange={() => toggleDuration(preset.value)}
                />
              }
              label={preset.label}
            />
          ))}
        </FormGroup>
        {errors.availableDurations && (
          <FormHelperText error>{errors.availableDurations}</FormHelperText>
        )}
      </Box>

      <AvailabilityWeekGrid
        weekSchedule={form.weekSchedule}
        onChange={(weekSchedule) => setField('weekSchedule', weekSchedule)}
        error={errors.availability}
      />

      <Typography variant="h6">Regole prenotazione</Typography>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <FormControlLabel
            control={
              <Switch
                checked={form.requiresApproval}
                onChange={(e) => setField('requiresApproval', e.target.checked)}
              />
            }
            label="Richiede approvazione admin"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            fullWidth
            type="number"
            label="Anticipo massimo (giorni)"
            value={form.advanceBookingDays}
            onChange={(e) => setField('advanceBookingDays', e.target.value)}
            error={Boolean(errors.advanceBookingDays)}
            helperText={errors.advanceBookingDays}
            inputProps={{ min: 1, max: 365 }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            fullWidth
            type="number"
            label="Cancellazione (ore prima)"
            value={form.cancellationHours}
            onChange={(e) => setField('cancellationHours', e.target.value)}
            error={Boolean(errors.cancellationHours)}
            helperText={errors.cancellationHours}
            inputProps={{ min: 0 }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ReservableFormFields;
